import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, ChevronRight } from 'lucide-react';
import { Button, Card } from '@employer-on-record/ui';
import { companiesApi } from '@/services/api';
import { PageHead } from '@/components/PageHead';
import { CompanyFormDrawer } from '@/components/modals/CompanyFormDrawer';
import { formatDateShort, formatNumber } from '@/lib/utils';
import type { CompanyDTO } from '@employer-on-record/shared';

export default function CompaniesPage() {
	const { data } = useQuery({ queryKey: ['admin-companies'], queryFn: () => companiesApi.list() });
	const companies = data?.data ?? [];
	const [drawer, setDrawer] = useState<{ open: boolean; mode: 'create' | 'edit'; company: CompanyDTO | null }>({
		open: false, mode: 'create', company: null
	});

	return (
		<>
			<PageHead
				title="Companies"
				subtitle={`${formatNumber(companies.length)} client compan${companies.length === 1 ? 'y' : 'ies'} onboarded`}
				actions={
					<Button onClick={() => setDrawer({ open: true, mode: 'create', company: null })}>
						<Plus className="h-3.5 w-3.5" /> Create company
					</Button>
				}
			/>

			<Card>
				<table className="w-full text-[13px]">
					<thead>
						<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
							<th className="text-left px-4 py-2.5 font-semibold">Company</th>
							<th className="text-left px-4 py-2.5 font-semibold">Industry</th>
							<th className="text-left px-4 py-2.5 font-semibold">HR</th>
							<th className="text-left px-4 py-2.5 font-semibold">Employees</th>
							<th className="text-left px-4 py-2.5 font-semibold">Approval levels</th>
							<th className="text-left px-4 py-2.5 font-semibold">Created</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{companies.length === 0 && (
							<tr><td colSpan={7} className="text-center text-muted-foreground p-10">No companies yet — click "Create company" to onboard your first client.</td></tr>
						)}
						{companies.map((c) => (
							<tr key={c.id} className="border-b last:border-0 hover:bg-background/60">
								<td className="px-4 py-3.5">
									<Link to={`/companies/${c.id}`} className="font-medium hover:underline">{c.name}</Link>
									{c.address && <div className="text-[11px] text-muted-foreground">{c.address}</div>}
								</td>
								<td className="px-4 py-3.5 text-muted-foreground">{c.industry || '—'}</td>
								<td className="px-4 py-3.5 tabular">{formatNumber(c.hrCount)}</td>
								<td className="px-4 py-3.5 tabular">{formatNumber(c.employeeCount)}</td>
								<td className="px-4 py-3.5 text-muted-foreground">Leave {c.leaveApprovalLevels} · Reimburse {c.reimbursementApprovalLevels}</td>
								<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(c.createdAt)}</td>
								<td className="px-4 py-3.5 text-right">
									<div className="inline-flex gap-1.5">
										<Button variant="secondary" size="sm" onClick={() => setDrawer({ open: true, mode: 'edit', company: c })}>
											<Pencil className="h-3 w-3" /> Edit
										</Button>
										<Button asChild variant="ghost" size="sm">
											<Link to={`/companies/${c.id}`}>Open <ChevronRight className="h-3 w-3" /></Link>
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</Card>

			<CompanyFormDrawer
				open={drawer.open}
				mode={drawer.mode}
				company={drawer.company}
				onClose={() => setDrawer({ open: false, mode: 'create', company: null })}
			/>
		</>
	);
}
